import s from "../mobile.module.css";
import {Button, Card, message, Modal, Typography, Upload, Image, List, Tooltip} from "antd";
import {CameraOutlined, PlusOutlined, DeleteOutlined, EyeOutlined} from "@ant-design/icons";
import React, {useCallback, useState, useEffect} from "react";
import {instance, production} from "../../../API/API";
import {useHeicConverter} from "../../../hooks/useHeicConverter";

const {Title, Text} = Typography;

const NewUpload = ({
                       photoUploaded,
                       steps,
                       object,
                       setCurrentStep,
                       setPhotoUploaded
                   }) => {
    const [fileList, setFileList] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const { convertHeicToJpeg, converting } = useHeicConverter();
    // Загрузка уже загруженных фотографий при монтировании
    useEffect(() => {
        fetchUploadedPhotos();
    }, [object.id]);

    // Функция для загрузки уже загруженных фотографий
    const fetchUploadedPhotos = async () => {
        try {
            const response = await instance.get(
                `/reports/getPhotos.php`,
                {
                    params: {
                        object_id: object.id
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true // важно для отправки кук
                }
            );

            if (response.data.success) {
                const formattedFiles = response.data.photos.map(photo => ({
                    uid: photo.id.toString(),
                    name: photo.original_name || `photo_${photo.id}`,
                    status: 'done',
                    url: `https://gdapp.org/data/${photo.link}`,
                    size: photo.size || 0,
                    uploaded: true,
                    id: photo.id,
                    link: photo.link
                }));
                setUploadedFiles(formattedFiles);
            }
        } catch (error) {
            console.error('Error fetching photos:', error);

            // Более детальная обработка ошибок
            if (error.response) {
                // Сервер ответил с кодом ошибки
                console.error('Server error:', error.response.status);
                console.error('Error data:', error.response.data);
            } else if (error.request) {
                // Запрос был сделан, но ответ не получен
                console.error('No response received:', error.request);
            } else {
                // Ошибка при настройке запроса
                console.error('Request setup error:', error.message);
            }
        }
    };


    const handleUpload = useCallback(async () => {
        if (fileList.length === 0) {
            message.warning('Выберите фотографии для загрузки');
            return;
        }

        setUploading(true);

        try {
            const formData = new FormData();

            // Правильное добавление нескольких файлов
            fileList.forEach(file => {
                // Добавляем все файлы с одним именем поля 'files[]'
                formData.append('files[]', file.originFileObj || file);
            });

            const response = await instance.post(
                `/reports/uploadReports.php`,
                formData,
                {
                    params: {
                        id: object.id // ID объекта передаем как query параметр
                    },
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true, // для отправки кук авторизации
                    timeout: 300000, // 5 минут таймаут для загрузки файлов
                    onUploadProgress: (progressEvent) => {
                        // Опционально: можно добавить отображение прогресса
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        console.log(`Upload progress: ${percentCompleted}%`);
                    }
                }
            );

            const result = response.data;

            if (result.success) {
                message.success(`Успешно загружено ${result.total_uploaded || fileList.length} фотографий`);

                // Обновляем список загруженных файлов
                await fetchUploadedPhotos();

                setFileList([]);
                setPhotoUploaded(true);

                // Переходим к следующему шагу только если есть загруженные файлы
                if (result.total_uploaded > 0) {
                    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
                }
            } else {
                throw new Error(result.error || 'Ошибка сервера');
            }
        } catch (error) {
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    }, [fileList, object.id, setPhotoUploaded, setCurrentStep, steps.length, fetchUploadedPhotos]);
    // Функция для удаления фотографии
    const handleDeletePhoto = async (file) => {
        try {
            const response = await instance.delete(
                `/reports/deletePhoto.php`,
                {
                    params: {
                        filename: file.link,
                        object_id: object.id
                    },
                    withCredentials: true, // для отправки кук авторизации
                    timeout: 10000, // 10 секунд таймаут
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                }
            );

            const result = response.data;

            if (result.success) {
                message.success('Фотография удалена');
                setUploadedFiles(prev => prev.filter(f => f.uid !== file.uid));

                // Если удалены все фото, сбрасываем статус загрузки
                if (uploadedFiles.length === 1) {
                    setPhotoUploaded(false);
                }
            } else {
                throw new Error(result.error || 'Ошибка удаления');
            }
        } catch (error) {
            console.error('Delete error:', error);
        }
    };

    const handlePreview = async (file) => {
        if (file.url) {
            setPreviewImage(file.url);
            setPreviewTitle(file.name);
            setPreviewVisible(true);
        } else if (file.originFileObj) {
            // Для HEIC файлов создаем временный URL
            if (file.originFileObj.type.includes('heic')) {
                try {
                    const convertedFile = await convertHeicToJpeg(file.originFileObj);
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        setPreviewImage(e.target.result);
                        setPreviewTitle(file.name);
                        setPreviewVisible(true);
                    };
                    reader.readAsDataURL(convertedFile);
                } catch (error) {
                    message.error('Не удалось открыть HEIC файл');
                }
            } else {
                // Для обычных файлов
                const reader = new FileReader();
                reader.onload = (e) => {
                    setPreviewImage(e.target.result);
                    setPreviewTitle(file.name);
                    setPreviewVisible(true);
                };
                reader.readAsDataURL(file.originFileObj);
            }
        }
    };

    // Обработчик добавления файлов

    const handleBeforeUpload = async (file) => {
        // Проверяем максимальное количество файлов
        if (fileList.length + uploadedFiles.length >= 20) {
            message.warning('Максимальное количество фотографий: 20');
            return false;
        }

        // Проверяем размер файла
        const maxSize = 200 * 1024 * 1024; // 200MB
        if (file.size > maxSize) {
            message.error(`Файл ${file.name} слишком большой. Максимальный размер: 200MB`);
            return false;
        }

        // Обрабатываем HEIC файлы
        let processedFile = file;
        if (file.type.includes('heic') || file.name.toLowerCase().endsWith('.heic')) {
            try {
                message.info('Конвертируем HEIC в JPEG...');
                processedFile = await convertHeicToJpeg(file);
                message.success('Фото сконвертировано');
            } catch (error) {
                message.error(error.message);
                return false;
            }
        }

        // Проверяем поддерживаемые форматы
        const supportedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!supportedFormats.includes(processedFile.type)) {
            message.error(`Формат ${processedFile.type} не поддерживается`);
            return false;
        }

        // Создаем объект с preview для нового файла
        const newFile = {
            uid: Math.random().toString(36).substr(2, 9), // Генерируем уникальный ID
            name: processedFile.name,
            status: 'done',
            originFileObj: processedFile,
            size: processedFile.size,
            type: processedFile.type
        };

        setFileList(prev => [...prev, newFile]);
        return false;
    };

    // Обработчик удаления файлов из списка новых
    const handleRemove = (file) => {
        if (file.uploaded) {
            // Для загруженных файлов вызываем удаление с сервера
            handleDeletePhoto(file);
            return false;
        } else {
            // Для новых файлов просто удаляем из списка
            setFileList(prev => prev.filter(f => f.uid !== file.uid));
        }
    };

    const uploadProps = {
        onRemove: handleRemove,
        beforeUpload: handleBeforeUpload,
        fileList: [...fileList, ...uploadedFiles],
        listType: "picture-card",
        onPreview: handlePreview,
        maxCount: 20,
        multiple: true,
        accept: "image/*",
        showUploadList: {
            showRemoveIcon: true,
            showPreviewIcon: true,
            removeIcon: <DeleteOutlined/>,
            previewIcon: <EyeOutlined/>
        }
    };

    const uploadButton = (
        <div className={s.uploadBtn}>
            <PlusOutlined/>
            <div className={s.uploadText}>Добавить фото</div>
        </div>
    );

    return (
        <div className={s.stepContent}>
            <Card className={s.uploadCard}>
                <div className={s.uploadHeader}>
                    <CameraOutlined className={s.uploadIcon}/>
                    <Title level={5} className={s.uploadTitle}>
                        Фотографии объекта
                    </Title>
                    <Text type="secondary">Максимум 20 фотографий</Text>
                </div>

                {/* Загрузка новых фотографий */}
                <div className={s.uploadSection}>
                    <Upload {...uploadProps} className={s.uploadContainer}>
                        {fileList.length + uploadedFiles.length >= 20 ? null : uploadButton}
                    </Upload>

                    {fileList.length > 0 && (
                        <Button
                            type="primary"
                            onClick={handleUpload}
                            loading={uploading}
                            disabled={uploading}
                            block
                            size="large"
                            className={s.uploadActionBtn}
                        >
                            {uploading ? 'Загружаем...' : `Загрузить ${fileList.length} фото`}
                        </Button>
                    )}
                </div>

                {/* Список загруженных фотографий */}
                {uploadedFiles.length > 0 && (
                    <div className={s.uploadedList}>
                        <Text strong style={{display: 'block', marginBottom: 12, marginTop: 16}}>
                            Загруженные фотографии ({uploadedFiles.length})
                        </Text>
                        {uploadedFiles ? uploadedFiles.map(file => {
                            return (
                                    <div className={s.cont__photo__list}>
                                        <div className={s.cont__photo__name}>
                                            <Tooltip title={file.name}>
                                                <div style={{cursor: 'pointer'}}>
                                                    {file.name.length > 25 ? `${file.name.substring(0, 25)}...` : file.name}
                                                </div>
                                            </Tooltip>
                                            <div style={{color: '#939393'}}>{`Размер: ${Math.round(file.size / 1024)} KB`}</div>
                                        </div>
                                        <div className={s.btn__photo__list}>
                                            <div  className={s.btn__border}>
                                                <Button
                                                    type="link"
                                                    icon={<EyeOutlined/>}
                                                    onClick={() => handlePreview(file)}
                                                    size="small"
                                                />
                                            </div>
                                            <div  className={s.btn__border}>
                                                <Button
                                                    type="link"
                                                    icon={<DeleteOutlined/>}
                                                    onClick={() => handleDeletePhoto(file)}
                                                    danger
                                                    size="small"
                                                />
                                            </div>



                                        </div>


                                    </div>
                            )
                        }) : null}
                    </div>
                )}

                {photoUploaded && uploadedFiles.length > 0 && (
                    <div className={s.uploadStatus}>
                        <Text type="success">
                            ✓ Загружено {uploadedFiles.length} фотографий
                        </Text>
                    </div>
                )}
            </Card>

            {/* Модальное окно предпросмотра */}
            <Modal
                open={previewVisible}
                title={previewTitle}
                footer={null}
                onCancel={() => setPreviewVisible(false)}
                centered
                width="80%"
            >
                <Image
                    alt="preview"
                    src={previewImage}
                    style={{width: '100%'}}
                    preview={false}
                />
            </Modal>
        </div>
    );
};

export default NewUpload;